import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Table,
  createTableColumn,
  useTableFeatures,
  useTableColumnSizing_unstable,
} from "@fluentui/react-components";
import TableHeaderContainer from "../../../Containers/RecordingsManagement/TableContainer/TableHeaderContainer";
import TableBodyContainer from "../../../Containers/RecordingsManagement/TableContainer/TableBodyContainer";
import ColumnManager from "./ColumnManager";
import "./CSS/TableComponent.css";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

const TableComponent = ({ data, filters, onFilterChange, columns, onColumnsChange, callFlows, supervisedAgentDetails }) => {
  const [filteredItems, setFilteredItems] = useState([]);
  const [isAudioPlayed, setIsAudioPlayed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(8); // Default
  
  // Track if any audio is playing to adjust Recording column width
  const hasAudioPlaying = useMemo(() => {
    return isAudioPlayed && typeof isAudioPlayed === 'object' && Object.values(isAudioPlayed).some(val => val === true);
  }, [isAudioPlayed]);


  // ⚡ Dynamically calculate rowsPerPage based on window height
  const calculateRowsPerPage = () => {
    const windowHeight = window.innerHeight;
    const headerHeight = 60; // Table header height
    const paginationHeight = 80; // Pagination height
    const otherSpacing = 100; // Other margins and spacing
    const rowHeight = 35; // Single row height

    const availableHeight = windowHeight - headerHeight - paginationHeight - otherSpacing;
    const calculatedRows = Math.floor(availableHeight / rowHeight);
    
    setRowsPerPage(calculatedRows > 5 ? calculatedRows : 5);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      calculateRowsPerPage();
    }, 100);
    
    window.addEventListener("resize", calculateRowsPerPage);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", calculateRowsPerPage);
    };
  }, []);

  // Columns - based on user configuration
  const tableColumns = useMemo(() => {
    const columnMapping = {
      audio: "Recording",
      date: "date",
      direction: "Direction",
      recordingScope: "recordingScope",
      agentName: "agentName",
      callerId: "callerId",
      duration: "duration",
      transcription: "callTrancription",
      callId: "callId",
    };

    return columns
      .filter((col) => col.visible)
      .map((col) => createTableColumn({ columnId: columnMapping[col.key] }));
  }, [columns]);

  // Calculate column width
  const calculateColumnWidth = (columnId, data) => {
    if (data && data.length > 0) {
      let maxLength = Math.max(
        ...data.map((item) => (item[columnId] ? item[columnId].length : 0)),
        columnId.length
      );
      return Math.max(maxLength * 10, 150);
    }
  };

  const columnSizingOptions = useMemo(() => {
    const sizingOptions = {
      Recording: {
        minWidth: hasAudioPlaying ? 300 : 80,
        idealWidth: hasAudioPlaying ? 350 : 100,
      },
      Direction: {
        minWidth: 100,
        idealWidth: 110,
      },
      recordingScope: {
        minWidth: 140,
        idealWidth: 150,
      },
      callerId: {
        minWidth: 130,
        idealWidth: 150,
      },
      duration: {
        // Valeur affichée courte (00:03:18) ; on garde juste de quoi loger le filtre From/To.
        minWidth: 140,
        idealWidth: 150,
      },
      agentName: {
        minWidth: 150,
        idealWidth: 160,
      },
      date: {
        minWidth: 170,
        idealWidth: 180,
      },
      callTrancription: {
        minWidth: 110,
        idealWidth: 120,
      },
      callId: {
        // Id complet (GUID) affiché sans troncature — le scroll horizontal gère la largeur.
        minWidth: 300,
        idealWidth: 300,
      },
    };
    return sizingOptions;
  }, [hasAudioPlaying]);

  // ⚠️ useTableColumnSizing_unstable provoquait une boucle infinie de rendu
  // ("Maximum update depth exceeded") via son ResizeObserver + contexte Fluent
  // (bug connu, resté dormant tant que la table était vide). On garde
  // useTableFeatures pour getRows et on NEUTRALISE le sizing par un no-op :
  // les colonnes se dimensionnent via le CSS (look inchangé), plus de boucle.
  const { getRows } = useTableFeatures(
    { columns: tableColumns, items: filteredItems },
    []
  );
  const tableRef = useRef(null);
  // On NEUTRALISE le ResizeObserver (source de la boucle) mais on applique des
  // largeurs STATIQUES par colonne via un style inline (header + body passent par
  // getTableCellProps) : les colonnes gardent une largeur bornée et ne débordent
  // plus l'une sur l'autre (ex. Caller ID qui chevauchait Duration).
  const columnSizing_unstable = useMemo(
    () => ({
      getTableProps: () => ({}),
      getTableCellProps: (columnId) => {
        const opt = columnSizingOptions[columnId];
        if (!opt) return {};
        const w = opt.idealWidth || opt.minWidth;
        return {
          style: {
            minWidth: opt.minWidth,
            width: w,
            // grow=0 : les colonnes NE s'étirent PLUS pour remplir la table
            // (sinon grands vides entre colonnes avec le contenu centré).
            // Elles gardent leur largeur idéale ; shrink=1 pour rester responsive.
            flex: `0 1 ${w}px`,
            boxSizing: "border-box",
          },
        };
      },
    }),
    [columnSizingOptions]
  );

  // Pagination handler
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const rows = getRows();
  const currentRows = rows.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <ColumnManager columns={columns} onColumnsChange={onColumnsChange} />
      <div className="recording-table-container">
        <div className="recording-scroll-table">
        <Table
          ref={tableRef}
          {...columnSizing_unstable.getTableProps()}
          noNativeElements={true}
          size="small"
          aria-label="Filtered Recordings Table"
        >
          <TableHeaderContainer
            onFilterChange={onFilterChange}
            filters={filters}
            isAudioPlayed={isAudioPlayed}
            columnSizing_unstable={columnSizing_unstable}
            columns={columns}
            callFlows={callFlows}
            supervisedAgentDetails={supervisedAgentDetails}
          />
          <TableBodyContainer
            data={data}
            setIsAudioPlayed={setIsAudioPlayed}
            isAudioPlayed={isAudioPlayed}
            setFilteredItems={setFilteredItems}
            filteredItems={filteredItems}
            currentRows={currentRows}
            columnSizing_unstable={columnSizing_unstable}
            columns={columns}
          />
        </Table>
      </div>
      </div>
      {filteredItems.length > 0 && (
        <Stack spacing={2} alignItems="center" className="recording-pagination-container">
          <Pagination
            count={Math.ceil(filteredItems.length / rowsPerPage)}
            color="primary"
            page={currentPage}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
        </Stack>
      )}
    </>
  );
};

export default TableComponent;
